import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http'
import { catchError, first, map, tap } from 'rxjs/operators'
import { Observable, of } from 'rxjs'
import { Action } from '@ngrx/store'

import { NGXLogger } from 'ngx-logger'
import { v4 as UUIDV4 } from 'uuid'
import { environment } from '../../environments/environment'
import { CoverResponse, CustomerAttribute, CustomerInfoResponse } from '@api/models/CustomerInfoCall'
import { CustomerInfoActions, CustomerInfoTypes } from '../account/user-profile/user-profile.actions'
import { CustomFieldValue, UserData } from '../account/manage-subscription/store/manage-subscription.state'
import { OrderElement } from "../orders/data/orderelement";

const blankUserInfo = {
  firstName: '',
  lastName: '',
  customerNumber: '',
  customerSubNumber: '',
  email: '',
  phoneNumber: '',
  mobile: '',
  birthdate: '',
  customerSince: '',
  address: {
    firstName: '',
    lastName: '',
    street: '',
    street2: '',
    company: '',
    countryCode: '',
    postalCode: '',
    city: '',
  },
  uuid: '',
}

@Injectable()
export class HttpService {
  constructor(private readonly httpClient: HttpClient, private readonly logger: NGXLogger) {
    this.setIp()
  }

  ipAddress: string | undefined = undefined

  private setIp() {
    let json = 'https://ipv4.myexternalip.com/json'
    this.httpClient
      .get(json)
      .pipe()
      .subscribe(data => {
        let result = data as { ip: string }

        this.ipAddress = result?.ip
      })
  }

  getUserInfo(uuid: string, isUserAdmin?: boolean): Observable<Action<CustomerInfoTypes>> {
    return this.httpClient
      .post<CustomerInfoResponse>(
        `${environment.backendBaseUrl}/user-info`,
        { request_id: UUIDV4(), parameter: uuid } as UserRequestBody<string>,
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
          }),
        },
      )
      .pipe(
        first(),
        map(result => {
          this.logger.debug(result)
          if (result.parameter.customer_info.error.error_code < 0 && isUserAdmin)
            return CustomerInfoActions.customerInfoSuccess({
              payload: blankUserInfo,
            })
          let address = result.parameter.customer_info.address
          return CustomerInfoActions.customerInfoSuccess({
            payload: {
              firstName: address.firstname,
              lastName: address.lastname,
              customerNumber: address.erp_customer_nr,
              customerSubNumber: address.erp_customer_sub_nr,
              email: address.email,
              phoneNumber: address.phone,
              mobile: address.mobile,
              birthdate: address.birthdate,
              customerSince: address.customer_since,
              address: {
                firstName: address.firstname,
                lastName: address.lastname,
                street: address.street,
                street2: address.street2,
                company: address.company,
                countryCode: address.country_code,
                postalCode: address.postcode,
                city: address.city,
              },
              uuid: uuid,
            },
          })
        }),
        catchError(error => {
          this.logger.debug(error)
          return of(CustomerInfoActions.customerInfoFail())
        }),
      )
  }

  updateAddress(address: AddressUpdateRequest): Observable<CoverResponse<string>> {
    return this.httpClient
      .post<CoverResponse<string>>(
        `${environment.backendBaseUrl}/update-address`,
        {
          request_id: UUIDV4(),
          parameter: address,
        },
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
          }),
        },
      )
      .pipe(
        first(),
        tap(result => {
          this.logger.debug(result)
        }),
      )
  }

  getAttributesInfo(uuid: string): Observable<CoverResponse<CustomerAttribute[]>> {
    return this.httpClient
      .post<CoverResponse<CustomerAttribute[]>>(
        `${environment.backendBaseUrl}/attributes-info`,
        `{"request_id":"${UUIDV4()}","parameter":"${uuid}"}`,
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
          }),
        },
      )
      .pipe(
        first(),
        tap(result => {
          this.logger.debug(result)
        }),
      )
  }

  cancelSubscription(info: CancellationInfo): Observable<Response> {
    // todo: implement attribute type
    return this.httpClient
      .post<Response>(
        `${environment.backendBaseUrl}/cancel`,
        { request_id: UUIDV4(), parameter: info } as UserRequestBody<CancellationInfo>,
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
          }),
        },
      )
      .pipe(
        first(),
        tap(result => {
          this.logger.debug(result)
        }),
      )
  }

  /**
   * Gets session ID from C4API which is needed to call other C4API endpoints. Can also be used to link an address no/invoice no
   * to a COVER address
   *
   * @param uuidAuthent used when uuid of user is not yet linked to an address number in COVER
   */
  loginC4API(uuidAuthent: string | undefined): Observable<CoverResponse<C4LoginResponse>> {
    return this.httpClient
      .post<CoverResponse<C4LoginResponse>>(`${environment.backendBaseUrl}/login`, {
        request_id: UUIDV4(),
        parameter: {
          uuidAuthent: uuidAuthent,
        },
      })
      .pipe(
        first(),
        tap(result => {
          this.logger.debug(result)
        }),
      )
  }

  getUserPaymentMethods(info: C4PaymentMethodsRequest): Observable<CoverResponse<C4PaymentMethodDetail[]>> {
    return this.httpClient
      .post<CoverResponse<C4PaymentMethodDetail[]>>(`${environment.backendBaseUrl}/payment-methods`, {
        request_id: UUIDV4(),
        parameter: { sessionId: info.sessionId },
      })
      .pipe(
        first(),
        tap(result => {
          this.logger.debug(result)
        }),
      )
  }

  createDebitCharge(request: CreateDebitChargeRequest): Observable<CoverResponse<C4PaymentMethodCreationResponse>> {
    return this.httpClient
      .post<CoverResponse<C4PaymentMethodCreationResponse>>(`${environment.backendBaseUrl}/new-debit-charge`, {
        request_id: UUIDV4(),
        parameter: request,
      })
      .pipe(
        first(),
        tap(result => {
          this.logger.debug(result)
        }),
      )
  }

  setDebitChargeInactive(request: SetDebitChargeInactive): Observable<CoverResponse<C4PaymentMethodCreationResponse>> {
    return this.httpClient
      .post<CoverResponse<C4PaymentMethodCreationResponse>>(`${environment.backendBaseUrl}/deactivate-debit-charge`, {
        request_id: UUIDV4(),
        parameter: request,
      })
      .pipe(
        first(),
        tap(result => {
          this.logger.debug(result)
        }),
      )
  }

  getInvoiceDownloadLinks(requestBody: C4InvoicesRequest): Observable<HttpResponse<Blob>> {
    return this.httpClient.post<Blob>(
      `${environment.backendBaseUrl}/download-invoice`,
      {
        request_id: UUIDV4(),
        parameter: requestBody,
      },
      { responseType: 'blob' as 'json', observe: 'response' },
    )
  }

  downloadArticle(downloadUrl: string): Observable<CoverResponse<string>> {
    return this.httpClient.post<CoverResponse<string>>(`${environment.backendBaseUrl}/download-article`, {
      request_id: UUIDV4(),
      parameter: downloadUrl,
    })
  }

  savePaymentMethod({
    paymentId,
    subscriptionId,
    sessionId,
  }: {
    paymentId: string
    subscriptionId: string
    sessionId: string
  }): Observable<void> {
    return this.httpClient.post<void>(`${environment.backendBaseUrl}/update-subscription`, {
      request_id: UUIDV4(),
      parameter: {
        payment_id: paymentId,
        subscription_uuid: subscriptionId,
        session_id: sessionId,
      },
    })
  }

  getUserInfoById(userId: string): Observable<UserData | undefined> {
    return this.httpClient.get<UserData>(`${environment.backendBaseUrl}/user-subscriptions-info/${userId}`)
  }

  getCustomFieldValues(): Observable<CustomFieldValue[]> {
    return this.httpClient.get<CustomFieldValue[]>(`${environment.backendBaseUrl}/custom-fields`)
  }

  saveUserPref(userId: string, unsubscribeCover: boolean, userData: UserData): Observable<void> {
    const headers = new HttpHeaders({
      'Access-Control-Allow-Origin': '*',
      'X-Cover-IP': this.ipAddress ?? 'could not retrieve client ip', // Replace with your custom header name and value
    })

    return this.httpClient.post<void>(
      `${environment.backendBaseUrl}/user-subscriptions-info/${userId}`,
      {
        ...userData,
        unsubscribeCover,
      },
      { headers },
    )
  }

  // savePaymentMethod(paymentMethod: C4PaymentMethodDetail): Observable<void> {
  //   return this.httpClient.post<void>(`${environment.backendBaseUrl}/payment-methods`, {
  //     request_id: UUIDV4(),
  //     parameter: {
  //       sessionId: paymentMethod.ZAHLWEG_ID,
  //     },
  //   })
  // }

  saveComplaint(complaint: string, order: OrderElement): Observable<void> {
    return this.httpClient.post<void>(`${environment.backendBaseUrl}/complaint`, {
      attributeId: order.attributeId,
      orderName: order.name,
      orderNumber: order.orderNumber,
      message: complaint,
    })
  }

  revocateSubscription(order: OrderElement): Observable<Response> {
    // todo: implement attribute type
    return this.httpClient
      .post<Response>(
        `${environment.backendBaseUrl}/revocate`,
        {
          request_id: UUIDV4(),
          parameter: { attributeId: order.attributeId, orderName: order.name, orderNumber: order.orderNumber },
        } as UserRequestBody<Partial<OrderElement>>,
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
          }),
        },
      )
      .pipe(first(), tap(this.logger.debug))
  }
}

export interface CreateDebitChargeRequest {
  sessionId: string
  accountOwner: string
  iban: string
  bankName: string
  bic: string
  checkbox: boolean
}

export interface SetDebitChargeInactive extends Omit<CreateDebitChargeRequest, 'checkbox'> {
  lastTyp: string
  abbuchArt: string
  sepaSatzNr: number
  mandateReference: string
  c4pk: string
}

export interface C4PaymentMethodsRequest {
  sessionId: string
}

export interface C4PaymentMethodDetailRequest extends C4PaymentMethodsRequest {
  c4pk: string
}

export interface C4InvoicesRequest extends C4PaymentMethodDetailRequest {
  attributeId: string
}

interface InvoiceDownloadLinks {
  expiresIn: number
  downloadLinks: string[]
}

interface C4PaymentMethodsResponse {
  ZAHLWEGMASTERSSV: C4PaymentMethod[]
  RESPONSE_OK: number
  RESPONSE_ERROR: string
  RESPONSE_INFO: string
}

interface C4PaymentMethodCreationResponse {
  RESPONSE_OK: number
  RESPONSE_ERROR: string
}

export interface C4PaymentMethod {
  ZAHLWEG_ID: string
  ZAHLWEG_TYP: string
  ZAHLWEG_NAME: string
  ZAHLWEG_DATUM: number
  C4PK: string
  USTAT: string
  ADR_NR: number
}

export interface C4PaymentMethodDetail extends C4PaymentMethod {
  IBAN: string
  BIC: string
  INHABER: string
  BANKNAME: string
  MANDATS_REFERENZ: string
  LAST_TYP: string
  ABBUCH_ART: string
  SEPA_SATZ_NR: number
}

interface C4LoginResponse {
  RESPONSE_OK: string
  RESPONSE_ERROR: string
  SESSION_ID: string
  PORTAL_UUID: string
  PORTAL_ADR_NR: number | string
  DEF_ADR_GR: string
}

interface UserRequestBody<T> {
  request_id: string
  parameter: T
}

export interface CancellationInfo {
  attributeId: string
  attributeType: string
}

export interface RevocationInfo {
  attributeId: string
  attributeType: string
}

export interface User {
  username: string
  firstName: string
  lastName: string
  enabled: boolean
  emailVerified: boolean
}

interface UserResponseBody {
  request_id: string
  parameter: User
}

export interface AddressUpdateRequest {
  first_name: string
  last_name: string
  customer_number: string
  customer_sub_number: string
  phone_number: string
  mobile: string
  email: string
  street: string
  street_2: string
  postal_code: string
  city: string
  company: string
  country_code: string
  birthdate: string
}
